// Champion identity on the client (POC 2026-10-09, office 14c9a221) :
//  • « Assignee is any of sven » returned 0 stories while Sven held roles on
//    23 missions — story assignees are often legacy mission-scoped agents
//    (« Sven (Orchestration) ») whose champion is COALESCE(staff_agent_id, id).
//    The feed exposes `champion_id` / `champion_name` / `champion_root_slug` ;
//    filtering and grouping go through the helpers below.
//  • The staff page showed raw mission uuids : `missionLabel` never returns one.
// Pure : no React, no i18n instance (callers pass `t`).

import { championFirstName } from './catalog.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HEX_ID_RE = /^[0-9a-f]{24,64}$/i;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)+$/;

/** True for a value that reads as a machine id (uuid / long hex), never a title. */
export function looksLikeId(value) {
  const s = String(value ?? '').trim();
  return UUID_RE.test(s) || HEX_ID_RE.test(s);
}

/** « Camille (Track) » → « Camille » : legacy runtime rows carried a role suffix. */
export function stripLegacySuffix(name) {
  const s = String(name ?? '').trim();
  const stripped = s.replace(/\s*\([^()]*\)\s*$/, '').trim();
  return stripped || s;
}

function capitalize(word) {
  return word ? word[0].toUpperCase() + word.slice(1) : '';
}

/**
 * A champion's display name : the catalog first name (« André »), else the
 * stored display name without its legacy suffix, else the slug's first word
 * capitalised (« bruno-builder » → « Bruno »). Never a slug, never an id.
 *
 * @param {{ champion_slug?: string|null, display_name?: string|null }} agent
 */
export function championDisplayName(agent) {
  const slug = agent?.champion_slug ?? null;
  const fromCatalog = slug ? championFirstName(slug) : null;
  if (fromCatalog) return fromCatalog;
  const stored = stripLegacySuffix(agent?.display_name ?? '');
  if (stored && !looksLikeId(stored) && !SLUG_RE.test(stored)) return stored;
  const base = slug || (SLUG_RE.test(stored) ? stored : '');
  if (base) return capitalize(base.split('-')[0]);
  return stored && !looksLikeId(stored) ? stored : '';
}

/**
 * The champion an assignee resolves to. Agents : COALESCE(staff_agent_id, id)
 * as served by the feed (`champion_id`), named after the champion row. Humans
 * stay themselves.
 *
 * @param {object} a feed assignee
 * @returns {{ key: string, id: string, kind: 'agent'|'human', name: string, slug: string|null }}
 */
export function championOfAssignee(a) {
  if (!a) return null;
  if (a.kind === 'human') {
    return { key: `human:${a.id}`, id: a.id, kind: 'human', name: a.display_name ?? '', slug: null };
  }
  const id = a.champion_id ?? a.id;
  const slug = a.champion_root_slug ?? a.champion_slug ?? null;
  const name = championDisplayName({ champion_slug: slug, display_name: a.champion_name ?? a.display_name });
  return { key: `agent:${id}`, id, kind: 'agent', name, slug };
}

/**
 * Every lowercase token an assignee answers to in a filter : its own id and
 * name, and its champion's id, name, first name and slug (+ the slug's first
 * word). « sven » therefore matches « Sven (Orchestration) », a legacy row of
 * sven-supervisor, and the office champion itself.
 */
export function assigneeTokens(a) {
  if (!a) return [];
  if (typeof a === 'string') return [a.trim().toLowerCase()].filter(Boolean);
  const out = new Set();
  const add = (v) => {
    const s = String(v ?? '').trim().toLowerCase();
    if (s) out.add(s);
  };
  add(a.id);
  add(a.display_name);
  add(stripLegacySuffix(a.display_name));
  if (a.kind !== 'human') {
    const c = championOfAssignee(a);
    add(c.id);
    add(c.name);
    add(stripLegacySuffix(a.champion_name));
    for (const slug of [c.slug, a.champion_slug]) {
      if (!slug) continue;
      add(slug);
      add(slug.split('-')[0]);
    }
  }
  return [...out];
}

/** Champions of a story (deduplicated, agents and humans), in assignee order. */
export function championsOfStory(story) {
  const seen = new Map();
  for (const a of story?.assignees ?? []) {
    const c = championOfAssignee(a);
    if (c && !seen.has(c.key)) seen.set(c.key, c);
  }
  return [...seen.values()];
}

function toMillis(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n < 1e12 ? n * 1000 : n; // seconds or milliseconds
}

/**
 * A mission's readable label : its title, or « Mission sans titre · 08/10 »
 * (`t('staff.untitledMission', { date })`) for an untitled / id-like legacy
 * title. Never a raw uuid.
 *
 * @param {{ title?: string|null, mission_id?: string, id?: string, created_at?: number|null }} m
 * @param {(key: string, opts?: object) => string} t
 * @param {string} [lang]
 */
export function missionLabel(m, t, lang = 'fr') {
  const title = String(m?.title ?? '').trim();
  const id = m?.mission_id ?? m?.id ?? '';
  if (title && title !== id && !looksLikeId(title)) return title;
  const ms = toMillis(m?.created_at);
  const date = ms == null
    ? null
    : new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'fr-FR', { day: '2-digit', month: '2-digit' }).format(new Date(ms));
  return date ? t('staff.untitledMissionOn', { date }) : t('staff.untitledMission');
}
